import * as React from 'react'
import {
  ButtonBase,
  capitalize,
  unsupportedProp,
  useForkRef,
  withStyles
} from '@material-ui/core'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import CancelIcon from '@material-ui/icons/Cancel'
import { chipColorStyles } from './DataDisplay.styles'

function isDeleteKeyboardEvent(keyboardEvent) {
  return keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete'
}

/**
 * Chips represent complex entities in small blocks, such as a contact.
 */
const ChipColor = React.forwardRef(function Chip(props, ref) {
  const {
    avatar: avatarProp,
    classes,
    className,
    clickable: clickableProp,
    color = 'default',
    component: ComponentProp,
    deleteIcon: deleteIconProp,
    disabled = false,
    icon: iconProp,
    label,
    onClick,
    onDelete,
    onKeyDown,
    onKeyUp,
    size = 'medium',
    variant = 'default',
    ...other
  } = props

  const chipRef = React.useRef(null)
  const handleRef = useForkRef(chipRef, ref)

  const handleDeleteIconClick = event => {
    // Stop the event from bubbling up to the `Chip`
    event.stopPropagation()
    if (onDelete) {
      onDelete(event)
    }
  }

  const handleKeyDown = event => {
    // Ignore events from children of `Chip`.
    if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
      // will be handled in keyUp, otherwise some browsers
      // might init navigation
      event.preventDefault()
    }

    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  const handleKeyUp = event => {
    // Ignore events from children of `Chip`.
    if (event.currentTarget === event.target) {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete(event)
      } else if (event.key === 'Escape' && chipRef.current) {
        chipRef.current.blur()
      }
    }

    if (onKeyUp) {
      onKeyUp(event)
    }
  }

  const clickable = clickableProp !== false && onClick ? true : clickableProp
  const small = size === 'small'

  const Component = ComponentProp || (clickable ? ButtonBase : 'div')
  const moreProps = Component === ButtonBase ? { component: 'div' } : {}

  let deleteIcon = null
  if (onDelete) {
    const customClasses = clsx({
      [classes.deleteIconSmall]: small,
      [classes[`deleteIconColor${capitalize(color)}`]]:
        color !== 'default' && variant !== 'outlined',
      [classes[`deleteIconOutlinedColor${capitalize(color)}`]]:
        color !== 'default' && variant === 'outlined'
    })

    deleteIcon =
      deleteIconProp && React.isValidElement(deleteIconProp) ? (
        React.cloneElement(deleteIconProp, {
          className: clsx(
            deleteIconProp.props.className,
            classes.deleteIcon,
            customClasses
          ),
          onClick: handleDeleteIconClick
        })
      ) : (
        <CancelIcon
          className={clsx(classes.deleteIcon, customClasses)}
          onClick={handleDeleteIconClick}
        />
      )
  }

  let avatar = null
  if (avatarProp && React.isValidElement(avatarProp)) {
    avatar = React.cloneElement(avatarProp, {
      className: clsx(classes.avatar, avatarProp.props.className, {
        [classes.avatarSmall]: small,
        [classes[`avatarColor${capitalize(color)}`]]: color !== 'default'
      })
    })
  }

  let icon = null
  if (iconProp && React.isValidElement(iconProp)) {
    icon = React.cloneElement(iconProp, {
      className: clsx(classes.icon, iconProp.props.className, {
        [classes.iconSmall]: small,
        [classes[`iconColor${capitalize(color)}`]]: color !== 'default'
      })
    })
  }

  if (process.env.NODE_ENV !== 'production') {
    if (avatar && icon) {
      console.error(
        'Material-UI: The Chip component can not handle the avatar ' +
          'and the icon prop at the same time. Pick one.'
      )
    }
  }

  return (
    <Component
      role={clickable || onDelete ? 'button' : undefined}
      className={clsx(
        classes.root,
        {
          [classes.disabled]: disabled,
          [classes.sizeSmall]: small,
          [classes[`color${capitalize(color)}`]]: color !== 'default',
          [classes.clickable]: clickable,
          [classes[`clickableColor${capitalize(color)}`]]:
            clickable && color !== 'default',
          [classes.deletable]: onDelete,
          [classes[`deletableColor${capitalize(color)}`]]:
            onDelete && color !== 'default',
          [classes.outlined]: variant === 'outlined',
          [classes.outlinedSuccess]:
            variant === 'outlined' && color === 'success',
          [classes.outlinedSecondary]:
            variant === 'outlined' && color === 'secondary'
        },
        className
      )}
      aria-disabled={disabled ? true : undefined}
      tabIndex={clickable || onDelete ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      ref={handleRef}
      {...moreProps}
      {...other}
    >
      {avatar || icon}
      <span
        className={clsx(classes.label, {
          [classes.labelSmall]: small
        })}
      >
        {label}
      </span>
      {deleteIcon}
    </Component>
  )
})

ChipColor.propTypes = {
  avatar: PropTypes.element,
  children: unsupportedProp,
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  clickable: PropTypes.bool,
  color: PropTypes.oneOf(['default', 'success', 'secondary']),
  component: PropTypes /* @typescript-to-proptypes-ignore */.elementType,
  deleteIcon: PropTypes.element,
  disabled: PropTypes.bool,
  icon: PropTypes.element,

  label: PropTypes.node,

  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'small']),
  variant: PropTypes.oneOf(['default', 'outlined'])
}

export default withStyles(chipColorStyles, { name: 'MuiChipColor' })(ChipColor)
